import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { Api } from "../api";
import { wavemindIcon } from "../utils/icons";

@customElement("register-page")
export class RegisterPage extends BaseElement {
    @state()
    private loading = false;

    @state()
    private error?: string;

    @state()
    private name = "";

    @state()
    private email = "";

    @state()
    private password = "";

    @state()
    private invitationCode: string = "";

    async connectedCallback() {
        super.connectedCallback();
        // Get invitation code from URL
        const params = new URLSearchParams(window.location.search);
        this.invitationCode = params.get("code") || "";

        if (!this.invitationCode) {
            this.error = "Invalid invitation code";
        }
    }

    private async handleSubmit(e: Event) {
        e.preventDefault();
        this.loading = true;
        this.error = undefined;

        try {
            await Api.register(this.name, this.email, this.password, this.invitationCode);
            // After successful registration, log the user in
            await Api.login(this.email, this.password);
            window.location.href = "/search";
        } catch (err) {
            this.error = err instanceof Error ? err.message : "Registration failed";
        } finally {
            this.loading = false;
        }
    }

    render() {
        return html`
            <div class="min-h-screen bg-gray-100 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
                <div class="max-w-md w-full mx-auto space-y-6 sm:space-y-8">
                    <!-- Logo and Title -->
                    <div class="text-center">
                        <div class="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-blue-600">
                            <i class="w-full h-full icon">${wavemindIcon}</i>
                        </div>
                        <h1 class="mt-3 sm:mt-4 text-3xl sm:text-4xl font-bold text-gray-900">WaveMind</h1>
                        <div class="mt-2 space-y-1">
                            <p class="text-lg font-medium text-gray-800">Create your account</p>
                            <p class="text-sm text-gray-600">Join WaveMind to explore media content with AI assistance</p>
                        </div>
                    </div>

                    <!-- Registration Form -->
                    <div class="bg-white py-6 sm:py-8 px-4 shadow-sm rounded-lg sm:px-10">
                        ${this.invitationCode
                            ? html`
                                  <form class="space-y-4 sm:space-y-6" @submit=${this.handleSubmit}>
                                      <div>
                                          <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
                                          <input
                                              type="text"
                                              id="name"
                                              required
                                              .value=${this.name}
                                              @input=${(e: InputEvent) => (this.name = (e.target as HTMLInputElement).value)}
                                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                                                focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                                h-10 px-3 bg-gray-50"
                                          />
                                      </div>

                                      <div>
                                          <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                                          <input
                                              type="email"
                                              id="email"
                                              required
                                              .value=${this.email}
                                              @input=${(e: InputEvent) => (this.email = (e.target as HTMLInputElement).value)}
                                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                                                focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                                h-10 px-3 bg-gray-50"
                                          />
                                      </div>

                                      <div>
                                          <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
                                          <input
                                              type="password"
                                              id="password"
                                              required
                                              minlength="8"
                                              .value=${this.password}
                                              @input=${(e: InputEvent) => (this.password = (e.target as HTMLInputElement).value)}
                                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                                                focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                                h-10 px-3 bg-gray-50"
                                          />
                                      </div>

                                      ${this.error ? html`<div class="text-red-600 text-sm">${this.error}</div>` : null}

                                      <button
                                          type="submit"
                                          ?disabled=${this.loading}
                                          class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                        disabled:bg-blue-400 disabled:cursor-not-allowed"
                                      >
                                          ${this.loading ? "Creating account..." : "Create account"}
                                      </button>

                                      <p class="text-center text-xs text-gray-500">
                                          Already have an account?
                                          <a href="/" class="text-blue-600 hover:text-blue-500">Sign in</a>
                                      </p>
                                  </form>
                              `
                            : html`
                                  <div class="text-center">
                                      <p class="text-sm text-red-600">Invalid or missing invitation code.</p>
                                      <a href="/" class="mt-4 inline-block text-sm text-blue-600 hover:text-blue-500">Back to sign in</a>
                                  </div>
                              `}
                    </div>
                </div>
            </div>
        `;
    }
}
